/**
 * Panel Tabs
 * Android Studio-style collapsible bottom panel with resizable height
 */

import React, { useCallback, useRef, useEffect } from 'react';
import { Ruler, Palette, Pencil, Upload, Layers, Play, LucideIcon, ChevronDown, ChevronUp, SlidersHorizontal, Image, Eye, Settings } from 'lucide-react';
import { useEditorStore } from '@/stores';
import { FormatPanel } from './FormatPanel';
import { FormatOverridesPanel } from './FormatOverridesPanel';
import { TemplatePanel } from './TemplatePanel';
import { ContentPanel } from './ContentPanel';
import { ExportPanel } from './ExportPanel';
import { LayerPanel } from './LayerPanel';
import { AnimatePanel } from './AnimatePanel';
import { AssetPanel } from './AssetPanel';
import { PreviewPanel } from './PreviewPanel';
import { PropertiesPanel } from './PropertiesPanel';

type PanelId = 'format' | 'template' | 'layers' | 'assets' | 'content' | 'responsive' | 'properties' | 'preview' | 'animate' | 'export';

interface TabConfig {
  id: PanelId;
  label: string;
  icon: LucideIcon;
}

const TABS: TabConfig[] = [
  { id: 'format', label: 'Format', icon: Ruler },
  { id: 'template', label: 'Templates', icon: Palette },
  { id: 'layers', label: 'Layers', icon: Layers },
  { id: 'assets', label: 'Resources', icon: Image },
  { id: 'content', label: 'Variables', icon: Pencil },
  { id: 'responsive', label: 'Responsive', icon: SlidersHorizontal },
  { id: 'properties', label: 'Properties', icon: Settings },
  { id: 'preview', label: 'Preview', icon: Eye },
  { id: 'animate', label: 'Animate', icon: Play },
  { id: 'export', label: 'Export', icon: Upload },
];

const PANEL_COMPONENTS: Record<PanelId, React.ComponentType> = {
  format: FormatPanel,
  template: TemplatePanel,
  layers: LayerPanel,
  assets: AssetPanel,
  content: ContentPanel,
  responsive: FormatOverridesPanel,
  properties: PropertiesPanel,
  preview: PreviewPanel,
  animate: AnimatePanel,
  export: ExportPanel,
};

const MIN_HEIGHT = 150;
const MAX_HEIGHT = 600;
const DEFAULT_HEIGHT = 280;

export function PanelTabs() {
  const activePanel = useEditorStore((s) => s.activePanel) as PanelId | null;
  const setActivePanel = useEditorStore((s) => s.setActivePanel);
  const [panelHeight, setPanelHeight] = React.useState(DEFAULT_HEIGHT);
  const [isResizing, setIsResizing] = React.useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(0);

  const handleTabClick = (panelId: PanelId) => {
    if (activePanel === panelId) {
      // Clicking active tab collapses the panel
      setActivePanel(null as any);
    } else {
      setActivePanel(panelId);
    }
  };

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = panelHeight;
  }, [panelHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = resizeStartY.current - e.clientY;
      const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, resizeStartHeight.current + delta));
      setPanelHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const PanelComponent = activePanel ? PANEL_COMPONENTS[activePanel] : null;
  const isOpen = activePanel !== null;

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-40 flex flex-col"
      style={{ pointerEvents: 'none' }}
    >
      {/* Panel content area */}
      {isOpen && PanelComponent && (
        <div
          className="bg-navy-dark/95 backdrop-blur-md border-t border-white/10 shadow-2xl overflow-hidden"
          style={{
            height: panelHeight,
            pointerEvents: 'auto',
          }}
        >
          {/* Resize handle */}
          <div
            className="absolute top-0 left-0 right-0 h-1 cursor-ns-resize hover:bg-primary/50 transition-colors z-10"
            onMouseDown={handleResizeStart}
          />

          {/* Panel header with close */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
            <h3 className="text-sm font-medium text-white">
              {TABS.find(t => t.id === activePanel)?.label}
            </h3>
            <button
              onClick={() => setActivePanel(null)}
              className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded"
            >
              <ChevronDown size={16} />
            </button>
          </div>

          {/* Panel content */}
          <div
            className="overflow-auto p-4"
            style={{ height: panelHeight - 41 }}
          >
            <PanelComponent />
          </div>
        </div>
      )}

      {/* Tab bar - always visible */}
      <div
        className="flex items-center bg-navy-dark border-t border-white/10"
        style={{ pointerEvents: 'auto' }}
      >
        {/* Scrollable tabs container */}
        <div className="flex-1 overflow-x-auto scrollbar-none">
          <div className="flex items-center">
            {TABS.map((tab) => {
              const isActive = activePanel === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-r border-white/5 whitespace-nowrap flex-shrink-0
                    ${isActive
                      ? 'bg-navy-light text-white border-t-2 border-t-primary -mt-[2px]'
                      : 'text-fg-muted hover:text-fg hover:bg-white/5'
                    }
                  `}
                >
                  <tab.icon size={14} className="flex-none" />
                  <span>{tab.label}</span>
                  {isActive && (
                    <ChevronUp size={12} className="ml-1 text-primary flex-none" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Panel height indicator when open */}
        {isOpen && (
          <span className="px-3 text-xs text-fg-subtle flex-shrink-0 border-l border-white/10">
            {panelHeight}px
          </span>
        )}
      </div>
    </div>
  );
}
